import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { auth } from '../services/firebase';
import { 
  verifyPasswordResetCode, 
  confirmPasswordReset, 
  applyActionCode,
  checkActionCode 
} from 'firebase/auth';
import { toast } from 'react-toastify';
import { Eye, EyeOff, CheckCircle, AlertCircle, Loader } from 'lucide-react';

const AuthAction: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [actionType, setActionType] = useState<string>('');
  const [actionCode, setActionCode] = useState<string>('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const mode = searchParams.get('mode');
    const oobCode = searchParams.get('oobCode');
    const continueUrl = searchParams.get('continueUrl');

    if (!mode || !oobCode) {
      setError('Invalid action link. Please try again.');
      setLoading(false);
      return;
    }

    setActionType(mode);
    setActionCode(oobCode);

    // Handle different action types
    handleAuthAction(mode, oobCode, continueUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleAuthAction = async (mode: string, oobCode: string, continueUrl: string | null) => {
    try {
      switch (mode) {
        case 'resetPassword':
          // Verify the password reset code and get email
          const email = await verifyPasswordResetCode(auth, oobCode);
          setEmail(email);
          setLoading(false);
          break;

        case 'verifyEmail':
          // Apply the email verification code
          await applyActionCode(auth, oobCode);
          setSuccess(true);
          setLoading(false);
          toast.success('Email verified successfully!');
          // Redirect after a short delay
          setTimeout(() => {
            navigate(continueUrl || '/dashboard');
          }, 2000);
          break;

        case 'recoverEmail':
          // Get info about the action
          const info = await checkActionCode(auth, oobCode);
          setEmail(info.data.email || '');
          setLoading(false);
          break;

        default:
          setError('Unknown action type.');
          setLoading(false);
      }
    } catch (error: any) {
      console.error('Auth action error:', error);
      let errorMessage = 'An error occurred. Please try again.';
      
      if (error.code === 'auth/invalid-action-code') {
        errorMessage = 'This link is invalid or has expired. Please request a new one.';
      } else if (error.code === 'auth/expired-action-code') {
        errorMessage = 'This link has expired. Please request a new one.';
      }
      
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }

    setProcessing(true);
    setError('');

    try {
      await confirmPasswordReset(auth, actionCode, newPassword);
      setSuccess(true);
      toast.success('Password reset successfully! You can now sign in with your new password.');
      
      // Redirect to login after a short delay
      setTimeout(() => {
        navigate('/login');
      }, 2000);
      
    } catch (error: any) {
      console.error('Password reset error:', error);
      let errorMessage = 'Failed to reset password. Please try again.';
      
      if (error.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak. Please choose a stronger password.';
      }
      
      setError(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  const handleEmailRecovery = async () => {
    setProcessing(true);
    setError('');

    try {
      await applyActionCode(auth, actionCode);
      setSuccess(true);
      toast.success('Email address recovered successfully!');
      
      // Redirect to dashboard after a short delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
      
    } catch (error: any) {
      console.error('Email recovery error:', error);
      setError('Failed to recover email address. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader size={48} className="animate-spin text-black mx-auto mb-4" />
          <p className="text-gray-600">Processing your request...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <AlertCircle size={48} className="text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Action Failed</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="btn-primary"
          >
            Go to Homepage
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
                          <CheckCircle size={48} className="text-gray-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Success!</h1>
          <p className="text-gray-600 mb-6">
            {actionType === 'resetPassword' && 'Your password has been reset successfully.'}
            {actionType === 'verifyEmail' && 'Your email address has been verified.'}
            {actionType === 'recoverEmail' && 'Your email address has been recovered.'}
          </p>
          <p className="text-sm text-gray-500">Redirecting you shortly...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8">
        {actionType === 'resetPassword' && (
          <>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Reset Your Password</h1>
            <p className="text-gray-600 mb-6">
              Enter a new password for <strong>{email}</strong>
            </p>

            <form onSubmit={handlePasswordReset} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="input-field pr-12"
                    placeholder="Enter new password"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm Password
                </label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input-field"
                  placeholder="Confirm new password"
                  required
                  minLength={6}
                />
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={processing || !newPassword || !confirmPassword}
                className="btn-primary w-full"
              >
                {processing ? (
                  <div className="flex items-center justify-center">
                    <Loader size={16} className="animate-spin mr-2" />
                    Resetting Password...
                  </div>
                ) : (
                  'Reset Password'
                )}
              </button>
            </form>
          </>
        )}

        {actionType === 'recoverEmail' && (
          <>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Recover Email Address</h1>
            <p className="text-gray-600 mb-6">
              Do you want to recover your email address to <strong>{email}</strong>?
            </p>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg mb-4">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <div className="space-y-3">
              <button
                onClick={handleEmailRecovery}
                disabled={processing}
                className="btn-primary w-full"
              >
                {processing ? (
                  <div className="flex items-center justify-center">
                    <Loader size={16} className="animate-spin mr-2" />
                    Recovering Email...
                  </div>
                ) : (
                  'Recover Email Address'
                )}
              </button>

              <button
                onClick={() => navigate('/')}
                className="btn-secondary w-full"
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthAction; 